import { LockOutlined, UserOutlined } from "@ant-design/icons";
import { Alert, Button, Form, Input } from "antd";
import { DEFAULT_LOGIN_REDIRECT_SCREEN } from "common/constants";
import { formatError } from "common/redux/middleware/queryErrorLogger";
import { resetStateAction } from "common/redux/resetStateAction";
import {
  useListLoginMethodsMutation,
  useLoginMutation,
  useLazyFetchMeQuery,
} from "features/auth/authAPI";
import { selectIsAccessTokenStoredInRtk, updateTokens } from "features/auth/authSlice";
import { useWhiteLabel } from "providers/whiteLabelProvider";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";

const StyledForm = styled(Form)`
  width: 300px;
`;

const StyledButton = styled(Button)`
  width: 100%;
  height: 50px;
`;

const StyledDiv = styled.div`
  margin-top: 10px;
  text-align: center;
`;

const BAD_REDIRECT_ROUTES = ["/login", "/signup", "/change-password"];

function LocalLoginForm() {
  const { isWhiteLabeled, displayName } = useWhiteLabel();
  const dispatch = useDispatch();
  const [errorMessage, setErrorMessage] = useState(null);
  const [login, { isLoading: isLoginLoading }] = useLoginMutation();
  const [listLoginMethods, { isLoading: isCheckLoading }] = useListLoginMethodsMutation();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const location = useLocation();
  const [fetchMe] = useLazyFetchMeQuery();
  const isAccessTokenStoredInRtk = useSelector(selectIsAccessTokenStoredInRtk);

  const [step, setStep] = useState("email");
  const [ssoUrl, setSsoUrl] = useState(null);

  useEffect(() => {
    if (isAccessTokenStoredInRtk) {
      dispatch(resetStateAction());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (location.state?.ssoError) {
      setErrorMessage(location.state.ssoError);
    }
  }, [location.state]);

  const redirectToDestination = async () => {
    // Fetch the optional defaultLoginRedirectScreen field from /me
    // to see if there is a specific page a user should see on login
    const { data: me, error } = await fetchMe();
    if (!me || "error" in me) {
      form.setFields(formatError(error));
      setErrorMessage("Something went wrong");
      throw new Error(error.data?.error?.code);
    }

    const defaultLoginRedirectScreen = `/${
      me?.defaultLoginRedirectScreen || DEFAULT_LOGIN_REDIRECT_SCREEN
    }`;

    const returnTo =
      location.state?.returnTo && BAD_REDIRECT_ROUTES.indexOf(location.state.returnTo) === -1
        ? location.state.returnTo
        : defaultLoginRedirectScreen;

    navigate(returnTo);
  };

  const onEmailContinue = async () => {
    try {
      await form.validateFields(["email"]);
    } catch {
      return;
    }

    setErrorMessage(null);
    const email = form.getFieldValue("email");
    const result = await listLoginMethods({ email });

    if ("error" in result) {
      setStep("password");
      return;
    }

    const methods = result.data;
    const ssoMethod = methods.find((m) => m.method === "sso");
    const hasPassword = methods.some((m) => m.method === "password");

    if (ssoMethod && !hasPassword) {
      window.location.href = ssoMethod.ssoUrl;
      return;
    }

    if (ssoMethod) {
      setSsoUrl(ssoMethod.ssoUrl);
    }

    setStep("password");
  };

  const onFinish = async (values) => {
    const { email, password } = values;

    const result = await login({ email, password });
    if ("data" in result) {
      await dispatch(updateTokens(result.data));
      try {
        await redirectToDestination();
      } catch (error) {
        if (error.message === "mfa_required") {
          if (result.data?.mfaDevices?.length > 0) {
            navigate("/otp-verification", {
              state: {
                requiredMfaDevices: result.data?.mfaDevices,
              },
            });
          } else {
            navigate("/initial-setup-mfa", {
              state: {
                showTopBar: false,
              },
            });
          }
        }
      }
    }
    if ("error" in result) {
      form.setFields(formatError(result.error));
      setErrorMessage("Invalid username or password");
    }
  };

  const handleSsoLogin = () => {
    if (ssoUrl) {
      window.location.href = ssoUrl;
    }
  };

  const handleBackToEmail = () => {
    setStep("email");
    setSsoUrl(null);
    setErrorMessage(null);
  };

  return (
    <>
      <h3>Log in to Your {isWhiteLabeled ? displayName : "Aktos"} Account</h3>
      <StyledForm
        onFinish={step === "email" ? onEmailContinue : onFinish}
        className="login-form"
        initialValues={{
          email: "",
          password: "",
        }}
        form={form}
      >
        <Form.Item
          name="email"
          hasFeedback
          rules={[
            { required: true, message: "Please enter email" },
            {
              type: "email",
              message: "Please enter a valid email",
            },
          ]}
        >
          <Input
            prefix={<UserOutlined />}
            placeholder="Enter email"
            data-testid="email"
            disabled={step === "password"}
          />
        </Form.Item>

        {step === "password" && (
          <>
            <Form.Item
              hasFeedback
              name="password"
              rules={[{ required: true, message: "Password is required" }]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                type="password"
                placeholder="Password"
                data-testid="password"
                autoFocus
              />
            </Form.Item>
            <Form.Item>
              <StyledButton
                type="primary"
                htmlType="submit"
                className="login-form-button"
                data-testid="login-button"
                loading={isLoginLoading}
              >
                Log in
              </StyledButton>
            </Form.Item>
            {ssoUrl && (
              <Form.Item>
                {/* TODO: make provider name dynamic once we support multiple SSO providers */}
                <StyledButton onClick={handleSsoLogin}>Log in with Okta</StyledButton>
              </Form.Item>
            )}
            <StyledDiv>
              <Button type="link" onClick={handleBackToEmail}>
                Use a different email
              </Button>
            </StyledDiv>
          </>
        )}

        {step === "email" && (
          <Form.Item>
            <StyledButton
              type="primary"
              htmlType="submit"
              data-testid="continue-button"
              loading={isCheckLoading}
            >
              Continue
            </StyledButton>
          </Form.Item>
        )}

        {errorMessage && (
          <Form.Item>
            <Alert message={errorMessage} type="error" />
          </Form.Item>
        )}
      </StyledForm>

      {step === "email" && (
        <StyledDiv>
          <a href="/signup">Have a client code? Sign up</a>
        </StyledDiv>
      )}
    </>
  );
}

export default LocalLoginForm;
